// ПЕРЕВОД параметров списков из формы Bitrix REST (filter/order/select/start — то, что
// шлют экраны) в запрос к своей базе (GET /api/own/* с плоскими параметрами crm_own_api.py).
//
// Экраны НЕ переписаны под новую форму намеренно: формы ОТВЕТОВ совпадают дословно,
// меняется только адрес. Разной осталась форма ЗАПРОСА — её и переводит этот модуль.
// Список того, что своя база умеет, закрытый (как в crm_own_api._где_*): непереводимый
// запрос НЕ обрезается молча — он честно объявляется непереводимым, и client.ts шлёт его
// в зеркало. Обрезать фильтр значило бы показать «все сделки» под видом «застоявшихся».
//
// Почему «в зеркало», а не ошибка. Непереводимы ровно запросы про то, чего в своей базе
// НЕТ КАК ДАННЫХ (время правки Bitrix, суда среди контактов, LIKE по коду стадии).
// Ошибка сломала бы работающие блоки (кабинет руководителя) ради принципа; зеркало
// отвечает на них правдой, а когда синк отстанет — честно пометит ответ _stale.
// Каждый такой случай назван причиной в поле `зеркало` — тесты держат список закрытым.

import type { ListParams } from './types'

/** Итог перевода: либо строка запроса к /api/own/*, либо причина остаться на зеркале. */
export type Перевод =
  | { вид: 'own'; параметры: string }
  | { вид: 'зеркало'; причина: string }

const своя = (п: URLSearchParams): Перевод => ({ вид: 'own', параметры: п.toString() })
const зеркало = (причина: string): Перевод => ({ вид: 'зеркало', причина })

// Поле «Судно» в сделке — тот же код, что DEAL_VESSEL_FIELD в lib/entities.ts и
// ПОЛЕ_СУДНА_СДЕЛКИ в crm_own_api.py. Продублирован строкой, а не импортом из lib/,
// нарочно: это контракт трёх сторон, и разъехаться он должен громко — тестом.
const ПОЛЕ_СУДНА = 'UF_CRM_1502613831'
// Судовые UF-поля контакта (VESSEL_FIELDS в lib/entities.ts): кто просит их в select,
// тот ждёт судов среди контактов — а в своей базе суда живут отдельно от людей.
const СУДОВЫЕ_ПОЛЯ_КОНТАКТА = ['UF_CRM_1508500822', 'UF_CRM_1545371205405',
                               'UF_CRM_1552383324552', 'UF_CRM_1552383346054']

/** Скаляр или массив скаляров → значение параметра. null — перевести нельзя. */
function списком(v: unknown): string | null {
  const части = (Array.isArray(v) ? v : [v])
    .map((x) => (typeof x === 'string' || typeof x === 'number' ? String(x).trim() : ''))
  if (части.some((x) => !x)) return null
  return части.join(',')
}

const скаляр = (v: unknown): string | null =>
  (typeof v === 'string' || typeof v === 'number') && String(v).trim() !== ''
    ? String(v).trim()
    : null

const ISO_ДЕНЬ = /^(\d{4})-(\d{2})-(\d{2})/

/** Дата из значения фильтра: «2026-07-01» или ISO с временем. Не дата — null. */
const день = (v: unknown): string | null => {
  const m = typeof v === 'string' ? ISO_ДЕНЬ.exec(v.trim()) : null
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null
}

/** Днём раньше: строгое «<2026-08-01» превращается во включительное «…-07-31» своей базы. */
function днёмРаньше(деньISO: string): string {
  const m = ISO_ДЕНЬ.exec(деньISO)!
  const t = Date.UTC(+m[1], +m[2] - 1, +m[3], 12) - 86_400_000
  const d = new Date(t)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`
}

/**
 * Сортировки сделок. Направление — часть ключа: у своей базы направление зашито в ядре
 * (_ПОРЯДОК_СДЕЛОК), и «OPPORTUNITY ASC» она не умеет — такой запрос честно едет в зеркало.
 *
 * DATE_MODIFY DESC → 'opened' — это решение ЯДРА, а не наша вольность: даты правки в своей
 * базе нет, и crm_own_api называет дату начала «ближайшей честной заменой» для «сначала
 * недавние» (см. комментарий у _ПОРЯДОК_СДЕЛОК). Экраны шлют этот порядок всегда.
 */
const ПОРЯДОК_СДЕЛОК: Record<string, string> = {
  'DATE_MODIFY DESC': 'opened',
  'BEGINDATE DESC': 'opened',
  'OPPORTUNITY DESC': 'amount',
  'TITLE ASC': 'title',
  'ID ASC': 'id',
}

/** Простые равенства фильтра сделок: ключ Bitrix → параметр /own/deals. */
const ФИЛЬТР_СДЕЛОК: Record<string, { имя: string; много?: boolean }> = {
  ID: { имя: 'id', много: true },
  '%TITLE': { имя: 'title' },
  COMPANY_ID: { имя: 'company', много: true },
  CONTACT_ID: { имя: 'contact', много: true },
  [ПОЛЕ_СУДНА]: { имя: 'vessel', много: true },
  ASSIGNED_BY_ID: { имя: 'assigned', много: true },
  CATEGORY_ID: { имя: 'category' },
  STAGE_ID: { имя: 'stage' },
}

/** Диапазоны дат сделок: оператор+поле Bitrix → параметр и надо ли сдвинуть границу. */
const ДАТЫ_СДЕЛОК: Record<string, { имя: string; строгое?: boolean }> = {
  '>=BEGINDATE': { имя: 'opened_from' },
  '<=BEGINDATE': { имя: 'opened_to' },
  '<BEGINDATE': { имя: 'opened_to', строгое: true },
  '>=CLOSEDATE': { имя: 'closed_from' },
  '<=CLOSEDATE': { имя: 'closed_to' },
  '<CLOSEDATE': { имя: 'closed_to', строгое: true },
}

function порядок(order: ListParams['order'], карта: Record<string, string>,
                 п: URLSearchParams): string | null {
  const пары = Object.entries(order ?? {})
  if (пары.length === 0) return null
  if (пары.length > 1) return `сортировка по ${пары.length} полям сразу`
  const ключ = `${пары[0][0]} ${String(пары[0][1]).toUpperCase()}`
  const своё = карта[ключ]
  if (своё === undefined) return `сортировка «${ключ}»`
  if (своё) п.set('order', своё)
  return null
}

/** Список сделок: filter/order/start → GET /own/deals. select не переводится — своя база
 *  отдаёт фиксированный набор колонок, и он покрывает всё, что экраны просят в select. */
export function переводСделок(params: ListParams): Перевод {
  const п = new URLSearchParams()
  for (const [ключ, значение] of Object.entries(params.filter ?? {})) {
    const простое = ФИЛЬТР_СДЕЛОК[ключ]
    if (простое) {
      const v = простое.много ? списком(значение) : скаляр(значение)
      if (v === null) return зеркало(`значение фильтра ${ключ} не скаляр и не список номеров`)
      п.set(простое.имя, v)
      continue
    }
    if (ключ === 'CLOSED') {
      if (значение !== 'Y' && значение !== 'N') return зеркало(`CLOSED=${String(значение)}`)
      п.set('closed', значение === 'Y' ? '1' : '0')
      continue
    }
    const дата = ДАТЫ_СДЕЛОК[ключ]
    if (дата) {
      const d = день(значение)
      if (!d) return зеркало(`${ключ}: значение не дата`)
      п.set(дата.имя, дата.строгое ? днёмРаньше(d) : d)
      continue
    }
    // Сюда попадают в т.ч. '<=DATE_MODIFY' (застоявшиеся сделки кабинета) и '%STAGE_ID'
    // (стадия без воронки): времени правки и LIKE по стадии в своей базе нет как данных.
    return зеркало(`фильтр «${ключ}» своя база не умеет`)
  }
  const беда = порядок(params.order, ПОРЯДОК_СДЕЛОК, п)
  if (беда) return зеркало(беда)
  if (params.start) п.set('start', String(params.start))
  return своя(п)
}

// У компаний и контактов сортировки по дате правки в своей базе нет вовсе (колонки нет).
// «DATE_MODIFY DESC» экраны шлют всегда — переводим в порядок по умолчанию (по номеру):
// это открытое решение, продиктованное ядром (_ПОРЯДОК_КОМПАНИЙ без 'modified' у own).
const ПОРЯДОК_КОМПАНИЙ: Record<string, string> = {
  'DATE_MODIFY DESC': '', 'TITLE ASC': 'title', 'ID ASC': 'id',
}
const ПОРЯДОК_ЛЮДЕЙ: Record<string, string> = {
  'DATE_MODIFY DESC': '', 'ID ASC': 'id',
}

const ФИЛЬТР_КОМПАНИЙ: Record<string, { имя: string; много?: boolean }> = {
  ID: { имя: 'id', много: true },
  '%TITLE': { имя: 'title' },
  ASSIGNED_BY_ID: { имя: 'assigned', много: true },
}

export function переводКомпаний(params: ListParams): Перевод {
  const п = new URLSearchParams()
  for (const [ключ, значение] of Object.entries(params.filter ?? {})) {
    const простое = ФИЛЬТР_КОМПАНИЙ[ключ]
    if (!простое) return зеркало(`фильтр «${ключ}» своя база не умеет`)
    const v = простое.много ? списком(значение) : скаляр(значение)
    if (v === null) return зеркало(`значение фильтра ${ключ} не скаляр и не список номеров`)
    п.set(простое.имя, v)
  }
  const беда = порядок(params.order, ПОРЯДОК_КОМПАНИЙ, п)
  if (беда) return зеркало(беда)
  if (params.start) п.set('start', String(params.start))
  return своя(п)
}

const ФИЛЬТР_ЛЮДЕЙ: Record<string, { имя: string; много?: boolean }> = {
  ID: { имя: 'id', много: true },
  '%FULL_NAME': { имя: 'name' },
  COMPANY_ID: { имя: 'company', много: true },
}

export function переводКонтактов(params: ListParams): Перевод {
  // Кто просит судовые UF-поля, тот ждёт судов среди контактов (Клиент 360° собирает
  // флот клиента из contacts). В своей базе судов среди людей НЕТ — отвечать «человеками
  // без судов» значило бы показать «флота нет». Такой запрос честно едет в зеркало.
  if ((params.select ?? []).some((s) => СУДОВЫЕ_ПОЛЯ_КОНТАКТА.includes(s))) {
    return зеркало('запрошены судовые поля контактов — в своей базе суда не контакты')
  }
  const п = new URLSearchParams()
  for (const [ключ, значение] of Object.entries(params.filter ?? {})) {
    const простое = ФИЛЬТР_ЛЮДЕЙ[ключ]
    // Сюда попадает '%LAST_NAME' («только суда» на экране контактов): отбор судов
    // фамилией — приём зеркала, в своей базе такой вопрос не имеет смысла.
    if (!простое) return зеркало(`фильтр «${ключ}» своя база не умеет`)
    const v = простое.много ? списком(значение) : скаляр(значение)
    if (v === null) return зеркало(`значение фильтра ${ключ} не скаляр и не список номеров`)
    п.set(простое.имя, v)
  }
  const беда = порядок(params.order, ПОРЯДОК_ЛЮДЕЙ, п)
  if (беда) return зеркало(беда)
  if (params.start) п.set('start', String(params.start))
  return своя(п)
}
